export interface BankFieldValues {
  clearing?: unknown;
  creditor?: unknown;
  creditorType?: unknown;
}

export const BANK_CREDITOR_COUNTRIES: ReadonlySet<string> = new Set(['NL', 'FR', 'BE', 'BR', 'IT']);

// Eurozone countries use the Signzy /eu endpoint — only holder name + IBAN (account number) required.
export const BANK_EUROZONE_COUNTRIES: ReadonlySet<string> = new Set([
  'AT',
  'HR',
  'CY',
  'EE',
  'FI',
  'DE',
  'GR',
  'IE',
  'LV',
  'LT',
  'LU',
  'MT',
  'PT',
  'SK',
  'SI',
  'ES',
]);

export const BANK_HOLDER_NUMBER_ONLY_COUNTRIES: ReadonlySet<string> = new Set([
  'AR',
  'MX',
  'PE',
  'TR',
  'AE',
  ...BANK_EUROZONE_COUNTRIES,
]);

// Countries whose Signzy call accepts/requires an isBusiness flag.
// CN and US use it to switch between personal/business endpoints; SA uses it
// together with creditorIdentifierType to pick the right identifier flavour.
export const BANK_IS_BUSINESS_COUNTRIES: ReadonlySet<string> = new Set(['CN', 'US', 'SA']);

// Countries whose provider call also requires a creditor identifier *type* discriminator.
// SA is the first such country: Signzy uses creditorIdentifierType to pick between
// NATIONAL_ID (individuals) and UNIFIED_NUMBER / COMMERCIAL_REGISTRATION (businesses).
export const BANK_CREDITOR_TYPE_REQUIRED_COUNTRIES: ReadonlySet<string> = new Set(['SA']);

// BR strictly requires the creditor identifier up-front before the Verify button
// is allowed (per the OLD pre-shared-policy switch). For NL/FR/BE/IT we keep the
// pre-PR-14568 Eurozone-style permissive behaviour: the button renders once
// holder + number are filled, and any provider-specific missing field is surfaced
// by the BE/SIGNZY response and mapped to a user-friendly message in bank.verifier.
const BANK_CREDITOR_REQUIRED_BEFORE_VERIFY: ReadonlySet<string> = new Set(['BR']);

function nonEmpty(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return true;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

export function bankReadyForCountry(cc: string, fields: BankFieldValues): boolean {
  if (BANK_CREDITOR_TYPE_REQUIRED_COUNTRIES.has(cc)) {
    return nonEmpty(fields.creditor) && nonEmpty(fields.creditorType);
  }
  if (BANK_CREDITOR_REQUIRED_BEFORE_VERIFY.has(cc)) return nonEmpty(fields.creditor);
  if (BANK_CREDITOR_COUNTRIES.has(cc)) return true;
  if (BANK_HOLDER_NUMBER_ONLY_COUNTRIES.has(cc)) return true;
  return nonEmpty(fields.clearing);
}
